/**
 * ConnectorRenderer - Renders connector elements
 * Supports straight, bent, and curved connectors with arrow heads and text labels
 */

import Konva from 'konva';
import { BaseRenderer } from '../core/BaseRenderer';
import { EditableElement, ConnectorElement, ConnectorData, AnchorPoint } from '../editing/types';
import { calculatePathPoints, getPointAlongPath, findClosestPointOnPath } from '../utils/connectorPathUtils';
import { getArrowHeadConfig, calculateArrowAngle } from '../utils/arrowRenderer';

export class ConnectorRenderer extends BaseRenderer {
  private pathNode: Konva.Line | Konva.Shape | null = null;
  private selectionHighlight: Konva.Line | null = null;
  private arrowNodes: Konva.Shape[] = [];
  private textGroup: Konva.Group | null = null;
  private textBackground: Konva.Shape | null = null;
  private textNode: Konva.Text | null = null;
  private controlPointNodes: Konva.Circle[] = [];
  
  private connectorData: ConnectorData;
  private _isSelectedState: boolean = false;

  constructor(element: ConnectorElement) {
    super(element);
    this.connectorData = element.data;
  }

  /**
   * Render the connector
   */
  render(): Konva.Node {
    // Clear any existing nodes
    this._node.destroyChildren();
    this.arrowNodes = [];
    this.controlPointNodes = [];
    
    // Calculate path points
    const pathPoints = this.calculatePath();
    
    // Create selection highlight (rendered first, below the connector)
    this.selectionHighlight = this.createSelectionHighlight(pathPoints);
    this._node.add(this.selectionHighlight);
    
    // Create the main path
    this.pathNode = this.createPath(pathPoints);
    this._node.add(this.pathNode);
    
    // Create arrow heads
    this.createArrowHeads(pathPoints);
    
    // Create text label if present
    if (this.connectorData.text) {
      this.createTextLabel(pathPoints);
    }
    
    return this._node;
  }

  /**
   * Calculate path points based on connector type
   * Converts absolute anchor coordinates to relative coordinates (relative to connector node position)
   */
  private calculatePath(): number[] {
    const { pathType, startAnchor, endAnchor, controlPoints } = this.connectorData;
    // Get absolute path points
    const absolutePath = calculatePathPoints(pathType, startAnchor, endAnchor, controlPoints);
    
    // Convert to relative coordinates (relative to connector's node position)
    // The connector node is positioned at element.x, element.y (bounding box minimum)
    const offsetX = this.element.x;
    const offsetY = this.element.y;
    
    const relativePath: number[] = [];
    for (let i = 0; i < absolutePath.length; i += 2) {
      const relX = absolutePath[i] - offsetX;
      const relY = absolutePath[i + 1] - offsetY;
      relativePath.push(relX, relY);
    }
    
    return relativePath;
  }

  /**
   * Get stroke width in pixels
   */
  private getStrokeWidthPx(): number {
    const strokeWidthMap: Record<string, number> = {
      'small': 3,
      'medium': 5,
      'large': 7,
      'xlarge': 9,
      'thin': 3,
      'thick': 7,
    };
    return strokeWidthMap[this.connectorData.strokeWidth] || 5;
  }

  /**
   * Create the main path based on connector type
   */
  private createPath(pathPoints: number[]): Konva.Line | Konva.Shape {
    const strokeWidth = this.getStrokeWidthPx();
    
    // If there's no text, show the complete connector path from start to end anchor
    // When there's text, we still shorten to make room for arrows (arrows will overlap slightly)
    const hasText = !!this.connectorData.text;
    const shortenedPath = hasText ? this.shortenPathForArrows(pathPoints) : pathPoints;
    
    if (this.connectorData.pathType === 'curved') {
      return this.createCurvedPath(shortenedPath, strokeWidth);
    } else {
      return this.createStraightOrBentPath(shortenedPath, strokeWidth);
    }
  }

  /**
   * Create curved (bezier) path
   */
  private createCurvedPath(pathPoints: number[], strokeWidth: number): Konva.Shape {
    return new Konva.Shape({
      stroke: this.connectorData.stroke,
      strokeWidth,
      dash: this.connectorData.dashEnabled ? [5, 5] : undefined,
      lineCap: 'round',
      lineJoin: 'round',
      perfectDrawEnabled: false,
      sceneFunc: (context, shape) => {
        context.beginPath();
        context.moveTo(pathPoints[0], pathPoints[1]);
        if (pathPoints.length >= 8) {
          context.bezierCurveTo(
            pathPoints[2], pathPoints[3],
            pathPoints[4], pathPoints[5],
            pathPoints[6], pathPoints[7]
          );
        } else {
          context.lineTo(pathPoints[pathPoints.length - 2], pathPoints[pathPoints.length - 1]);
        }
        context.stroke();
        context.fillStrokeShape(shape);
      },
      hitFunc: (context, shape) => {
        const originalPath = this.calculatePath();
        context.beginPath();
        context.moveTo(originalPath[0], originalPath[1]);
        if (originalPath.length >= 8) {
          context.bezierCurveTo(
            originalPath[2], originalPath[3],
            originalPath[4], originalPath[5],
            originalPath[6], originalPath[7]
          );
        } else {
          context.lineTo(originalPath[originalPath.length - 2], originalPath[originalPath.length - 1]);
        }
        context.lineWidth = 15;
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.stroke();
        context.fillStrokeShape(shape);
      },
    });
  }

  /**
   * Create straight or bent path
   */
  private createStraightOrBentPath(pathPoints: number[], strokeWidth: number): Konva.Line {
    return new Konva.Line({
      points: pathPoints,
      stroke: this.connectorData.stroke,
      strokeWidth,
      dash: this.connectorData.dashEnabled ? [5, 5] : undefined,
      lineCap: 'round',
      lineJoin: 'round',
      hitStrokeWidth: 15,
    });
  }

  /**
   * Create selection highlight
   */
  private createSelectionHighlight(pathPoints: number[]): Konva.Line {
    const strokeWidth = this.getStrokeWidthPx();
    
    return new Konva.Line({
      points: pathPoints,
      stroke: '#0066FF',
      strokeWidth: strokeWidth + 4,
      dash: [5, 5],
      opacity: 0.3,
      listening: false,
      visible: false,
    });
  }

  /**
   * Shorten path to make room for arrow heads
   */
  private shortenPathForArrows(pathPoints: number[]): number[] {
    if (pathPoints.length < 4) return pathPoints;
    
    const strokeWidth = this.getStrokeWidthPx();
    const arrowBaseSize = Math.max(10, strokeWidth * 3);
    const arrowLength = arrowBaseSize * 1.3;
    
    const result = [...pathPoints];
    
    // Shorten start if has arrow
    if (this.connectorData.arrowStart !== 'none') {
      const dx = pathPoints[2] - pathPoints[0];
      const dy = pathPoints[3] - pathPoints[1];
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > arrowLength) {
        const ratio = arrowLength / dist;
        result[0] = pathPoints[0] + dx * ratio;
        result[1] = pathPoints[1] + dy * ratio;
      }
    }
    
    // Shorten end if has arrow
    if (this.connectorData.arrowEnd !== 'none') {
      const len = pathPoints.length;
      const dx = pathPoints[len - 2] - pathPoints[len - 4];
      const dy = pathPoints[len - 1] - pathPoints[len - 3];
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > arrowLength) {
        const ratio = arrowLength / dist;
        result[len - 2] = pathPoints[len - 4] + dx * ratio;
        result[len - 1] = pathPoints[len - 3] + dy * ratio;
      }
    }
    
    return result;
  }

  /**
   * Create arrow heads
   * Uses the full path (not shortened) for arrow positioning when there's no text
   */
  private createArrowHeads(pathPoints: number[]): void {
    const strokeWidth = this.getStrokeWidthPx();
    
    // Use full path for arrow positioning (pathPoints passed in may be shortened)
    // We need the original full path to position arrows at the actual anchor points
    const fullPath = this.calculatePath();
    
    // Start arrow
    if (this.connectorData.arrowStart !== 'none') {
      const startAngle = calculateArrowAngle(fullPath, true);
      const arrowConfig = getArrowHeadConfig({
        x: fullPath[0],
        y: fullPath[1],
        angle: startAngle,
        style: this.connectorData.arrowStart,
        stroke: this.connectorData.stroke,
        strokeWidth,
      });
      
      if (arrowConfig) {
        const arrow = new Konva.Shape({
          fill: arrowConfig.fill,
          stroke: arrowConfig.stroke,
          strokeWidth: arrowConfig.strokeWidth,
          sceneFunc: arrowConfig.sceneFunc,
        });
        this._node.add(arrow);
        this.arrowNodes.push(arrow);
      }
    }
    
    // End arrow
    if (this.connectorData.arrowEnd !== 'none') {
      const endAngle = calculateArrowAngle(fullPath, false);
      const endX = fullPath[fullPath.length - 2];
      const endY = fullPath[fullPath.length - 1];
      
      const arrowConfig = getArrowHeadConfig({
        x: endX,
        y: endY,
        angle: endAngle,
        style: this.connectorData.arrowEnd,
        stroke: this.connectorData.stroke,
        strokeWidth,
      });
      
      if (arrowConfig) {
        const arrow = new Konva.Shape({
          fill: arrowConfig.fill,
          stroke: arrowConfig.stroke,
          strokeWidth: arrowConfig.strokeWidth,
          sceneFunc: arrowConfig.sceneFunc,
        });
        this._node.add(arrow);
        this.arrowNodes.push(arrow);
      }
    }
  }

  /**
   * Create text label
   */
  private createTextLabel(pathPoints: number[]): void {
    const textPos = getPointAlongPath(pathPoints, this.connectorData.textPosition);
    const textBgColor = this.connectorData.textBackgroundEnabled
      ? (this.connectorData.textBackgroundColor || this.connectorData.stroke)
      : undefined;
    
    this.textGroup = new Konva.Group({
      x: textPos.x,
      y: textPos.y,
      rotation: (textPos.angle * 180) / Math.PI,
      draggable: true,
    });
    
    // Background
    if (textBgColor) {
      this.textBackground = new Konva.Shape({
        fill: textBgColor,
        sceneFunc: (context, shape) => {
          context.font = '14px Inter, sans-serif';
          const metrics = context.measureText(this.connectorData.text || '');
          const padding = 4;
          const width = metrics.width + padding * 2;
          const height = 20;
          
          context.beginPath();
          context.rect(-width / 2, -height / 2, width, height);
          context.fill();
          context.fillStrokeShape(shape);
        },
      });
      this.textGroup.add(this.textBackground);
    }
    
    // Text
    this.textNode = new Konva.Text({
      text: this.connectorData.text,
      fontSize: 14,
      fontFamily: 'Inter, sans-serif',
      fill: textBgColor ? '#FFFFFF' : this.connectorData.stroke,
      align: 'center',
      verticalAlign: 'middle',
      offsetX: 0,
      offsetY: 0,
      listening: false,
    });
    
    // Center the text
    const textWidth = this.textNode.width();
    this.textNode.offsetX(textWidth / 2);
    this.textNode.offsetY(7); // Half of 14px font size
    
    this.textGroup.add(this.textNode);
    this._node.add(this.textGroup);
  }

  /**
   * Update the connector with new properties
   */
  update(updates: Partial<EditableElement>): void {
    this.updateElementData(updates);
    
    if (updates.data) {
      this.connectorData = { ...this.connectorData, ...updates.data };
    }
    
    // Rebuild the entire connector (simpler than partial updates for connectors)
    this.render();
    
    // Restore selection state
    if (this._isSelectedState) {
      this.setSelected(true);
    }
  }

  /**
   * Set selected state
   */
  setSelected(selected: boolean): void {
    super.setSelected(selected);
    this._isSelectedState = selected;
    
    if (this.selectionHighlight) {
      this.selectionHighlight.visible(selected);
    }
    
    // Show/hide control points for bent connectors
    if (selected && this.connectorData.pathType === 'bent') {
      this.showControlPoints();
    } else {
      this.hideControlPoints();
    }
  }

  /**
   * Show control points for bent connectors
   */
  private showControlPoints(): void {
    if (this.connectorData.pathType !== 'bent') return;
    
    const pathPoints = this.calculatePath();
    
    // For bent connectors, show the bend point(s)
    // The control points are at pathPoints[2,3] for the first bend, etc.
    if (pathPoints.length >= 4) {
      // Get mid-point for simple bent connector
      const midX = (pathPoints[0] + pathPoints[pathPoints.length - 2]) / 2;
      const midY = (pathPoints[1] + pathPoints[pathPoints.length - 1]) / 2;
      
      const controlPoint = new Konva.Circle({
        x: midX,
        y: midY,
        radius: 6,
        fill: '#FFFFFF',
        stroke: '#0066FF',
        strokeWidth: 2,
        draggable: true,
      });
      
      this._node.add(controlPoint);
      this.controlPointNodes.push(controlPoint);
    }
  }

  /**
   * Hide control points
   */
  private hideControlPoints(): void {
    this.controlPointNodes.forEach((node) => node.destroy());
    this.controlPointNodes = [];
  }

  /**
   * Get connector data
   */
  getConnectorData(): ConnectorData {
    return { ...this.connectorData };
  }

  /**
   * Get start anchor
   */
  getStartAnchor(): AnchorPoint {
    return { ...this.connectorData.startAnchor };
  }

  /**
   * Get end anchor
   */
  getEndAnchor(): AnchorPoint {
    return { ...this.connectorData.endAnchor };
  }

  /**
   * Get connected element IDs
   */
  getConnectedElementIds(): { start: string | null; end: string | null } {
    return {
      start: this.connectorData.startElementId,
      end: this.connectorData.endElementId,
    };
  }

  /**
   * Update anchor positions (when connected elements move)
   */
  updateAnchors(startAnchor?: AnchorPoint, endAnchor?: AnchorPoint): void {
    let changed = false;
    
    if (startAnchor) {
      this.connectorData.startAnchor = startAnchor;
      changed = true;
    }
    
    if (endAnchor) {
      this.connectorData.endAnchor = endAnchor;
      changed = true;
    }
    
    if (changed) {
      this.render();
      if (this._isSelectedState) {
        this.setSelected(true);
      }
    }
  }

  /**
   * Destroy the renderer
   */
  destroy(): void {
    this.pathNode = null;
    this.selectionHighlight = null;
    this.arrowNodes = [];
    this.textGroup = null;
    this.textBackground = null;
    this.textNode = null;
    this.controlPointNodes = [];
    super.destroy();
  }
}
